import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  Facebook,
  Landmark,
  Mail,
  QrCode,
  Send,
  ShieldCheck,
  Users,
} from "lucide-react";
import { LanguageSwitch } from "./NavbarTerritorialeTop";
import ProtocolloContattoModal from "../protocollo/ProtocolloContattoModal";

const COLUMNS = [
  {
    id: "piazza",
    title: "Piazza & Storie",
    links: [
      { to: "/piazze", label: "Piazza Social" },
      { to: "/eventi", label: "Eventi & Sagre" },
      { to: "/manifesto", label: "Manifesto" },
      { to: "/regole-piazza", label: "Regole della Piazza" },
    ],
  },
  {
    id: "mappe",
    title: "Mappe & Sentieri",
    links: [
      { to: "/esplora-la-mappa", label: "Mappa 3D dei borghi" },
      { to: "/qr", label: "Visore AR & QR" },
      { to: "/mappa-del-mondo", label: "Mappa del mondo" },
      { to: "/qr", label: "Guide QR" },
    ],
  },
  {
    id: "mondo",
    title: "Mondo & Alleanze",
    links: [
      { to: "/gemellaggi", label: "Gemellaggi" },
      { to: "/comuni-amici", label: "Comuni amici" },
      { to: "/mondo", label: "Mondo" },
      { to: "/universita", label: "Università" },
    ],
  },
  {
    id: "enti",
    title: "Enti & Assistenza",
    links: [
      { to: "/governance", label: "Governance" },
      { to: "/stanza-sindaci", label: "Stanza dei Sindaci" },
      { to: "/aderisci", label: "Adesione Enti & Università" },
      { to: "/richiedi-accesso", label: "Richiedi un accesso" },
      { to: "/ufficio-stampa", label: "Ufficio stampa" },
      { contact: true, label: "Contatta l'Ambasciatore" },
    ],
  },
];

const INFO_CARDS = [
  {
    to: "/qr",
    icon: QrCode,
    title: "Visore AR & QR Code",
    text: "Punta il telefono e apri i borghi in realtà aumentata.",
    testid: "footer-card-ar",
  },
  {
    to: "/gemellaggi",
    icon: Users,
    title: "Gemellaggi & Patti di Fratellanza",
    text: "La rete internazionale dei borghi amici.",
    testid: "footer-card-gemellaggi",
  },
  {
    to: "/governance",
    icon: Landmark,
    title: "Regione Abruzzo & Partner UE FESR",
    text: "Istituzioni, finanziamenti e trasparenza del progetto.",
    testid: "footer-card-fesr",
  },
];

const LEGAL = [
  { to: "/privacy", label: "Privacy" },
  { to: "/termini", label: "Termini" },
  { to: "/dati-societari", label: "Dati Societari" },
  { to: "/cookie-policy", label: "Cookie" },
];

function FooterColumn({ col, onContact }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#eedfcb] md:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`footer-col-${col.id}`}
        className="w-full min-h-[48px] flex items-center justify-between md:hidden text-left font-sans font-bold text-stone-950"
      >
        {col.title}
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      <h5 className="hidden md:block font-sans font-bold text-stone-950 mb-3 text-sm uppercase tracking-widest">
        {col.title}
      </h5>
      <ul
        id={`footer-col-${col.id}`}
        className={`${open ? "block" : "hidden"} md:block pb-4 md:pb-0 space-y-1`}
      >
        {col.links.map((l) => (
          <li key={(l.to || "contact") + l.label}>
            {l.contact ? (
              <button
                type="button"
                onClick={onContact}
                className="min-h-[44px] inline-flex items-center gap-1.5 text-sm text-stone-800 hover:text-[#a85837]"
              >
                <Mail size={14} aria-hidden />
                {l.label}
              </button>
            ) : l.external ? (
              <a
                href={l.to}
                target={l.to.startsWith("mailto:") ? undefined : "_blank"}
                rel={l.to.startsWith("mailto:") ? undefined : "noreferrer"}
                className="min-h-[44px] inline-flex items-center gap-1.5 text-sm text-stone-800 hover:text-[#a85837]"
              >
                <Mail size={14} aria-hidden />
                {l.label}
              </a>
            ) : (
              <Link
                to={l.to}
                className="min-h-[44px] inline-flex items-center text-sm text-stone-800 hover:text-[#a85837]"
              >
                {l.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function FooterTerritorialeCompleto() {
  const [nlEmail, setNlEmail] = useState("");
  const [nlDone, setNlDone] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const submitNl = (e) => {
    e.preventDefault();
    if (!nlEmail.trim()) return;
    try {
      localStorage.setItem("6amico_cartolina_domenica_draft", nlEmail.trim());
    } catch { /* noop */ }
    setNlDone(true);
  };

  return (
    <footer
      className="bg-[#f7efe6] border-t border-[#eedfcb] text-stone-800"
      data-testid="footer-territoriale"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <span
                className="w-11 h-11 rounded-full bg-[#c26d4a] text-white font-serif font-bold text-lg flex items-center justify-center border-2 border-[#a85837]"
                aria-hidden
              >
                6a
              </span>
              <span className="font-serif font-bold text-xl text-stone-950">6AMICO</span>
            </div>
            <p className="text-sm text-stone-800 leading-relaxed">
              Il social network dei borghi italiani: luoghi, storie, cammini e persone
              che li abitano.
            </p>
            <div className="mt-4 flex items-center gap-3" aria-label="Social ufficiali">
              <a
                href="https://www.facebook.com/aggiungimiaipreferiti/?locale=it_IT"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pagina Facebook ufficiale aggiungimiaipreferiti"
                data-testid="footer-facebook-aggiungimi"
                className="inline-flex items-center gap-2 rounded-full bg-[#162d4a] hover:bg-[#0f1f33] text-white px-4 py-2 text-xs font-semibold transition-colors"
              >
                <Facebook size={14} aria-hidden />
                aggiungimiaipreferiti™
              </a>
            </div>
            <Link
              to="/censisci"
              className="mt-4 inline-flex items-center justify-center min-h-[48px] px-5 rounded-full bg-stone-900 text-white font-sans font-semibold text-sm hover:bg-stone-700 transition-colors"
              data-testid="footer-cta-censisci"
            >
              + Censisci un Luogo o un Sentiero
            </Link>
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#ecd9c2] bg-[#fdfbf7] px-4 py-2 text-sm font-sans font-semibold text-stone-800 hover:border-[#c26d4a] hover:text-[#a85837] transition-colors break-all"
              data-testid="footer-mail-info"
              aria-label="Scrivici: info@6amico.com"
            >
              <Mail size={14} className="text-[#a85837] shrink-0" aria-hidden />
              info@6amico.com
            </button>
            <form onSubmit={submitNl} className="mt-5">
              <label
                htmlFor="footer-nl"
                className="block text-xs font-sans font-bold uppercase tracking-widest text-stone-800 mb-2"
              >
                La Cartolina della Domenica
              </label>
              <div className="flex gap-2">
                <input
                  id="footer-nl"
                  type="email"
                  value={nlEmail}
                  onChange={(e) => {
                    setNlEmail(e.target.value);
                    setNlDone(false);
                  }}
                  placeholder="La tua email"
                  className="flex-1 min-h-[48px] rounded-full border border-[#eedfcb] bg-white px-4 text-sm text-stone-950"
                />
                <button
                  type="submit"
                  className="min-h-[48px] min-w-[48px] rounded-full bg-[#c26d4a] hover:bg-[#a85837] text-white flex items-center justify-center"
                  aria-label="Iscriviti alla cartolina della domenica"
                >
                  <Send size={17} />
                </button>
              </div>
              {nlDone && (
                <p className="mt-2 text-xs font-sans font-semibold text-[#a85837]" role="status">
                  Bozza salvata sul dispositivo: l'iscrizione arriverà presto.
                </p>
              )}
            </form>
          </div>

          <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-6">
            {COLUMNS.map((c) => (
              <FooterColumn key={c.id} col={c} onContact={() => setContactOpen(true)} />
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {INFO_CARDS.map((card) => (
            <Link
              key={card.testid}
              to={card.to}
              data-testid={card.testid}
              className="rounded-2xl border border-[#ecd9c2] bg-[#fdfbf7] p-5 flex gap-4 hover:border-[#c26d4a] hover:shadow-md transition-all"
            >
              <span className="w-11 h-11 rounded-full bg-[#c26d4a]/15 text-[#a85837] flex items-center justify-center shrink-0" aria-hidden>
                <card.icon size={20} />
              </span>
              <span>
                <span className="block font-serif font-bold text-stone-950">{card.title}</span>
                <span className="block text-sm text-stone-800 mt-1">{card.text}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>

      <div className="border-t border-[#eedfcb] bg-[#fdfbf7]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex flex-col md:flex-row items-center gap-4 md:justify-between">
          <LanguageSwitch compact />
          <p className="text-xs font-sans text-stone-800 text-center order-last md:order-none">
            © 2013-2026 6amico.com · Marchio storico registrato in 27 nazioni aggiungimiaipreferiti™ · Fondatore: Paolo Pinti
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-4" aria-label="Link legali">
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="min-h-[44px] inline-flex items-center gap-1.5 text-xs font-sans font-semibold text-stone-800 hover:text-[#a85837]"
            >
              <Mail size={12} className="text-[#a85837]" aria-hidden />
              info@6amico.com
            </button>
            {LEGAL.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="min-h-[44px] inline-flex items-center text-xs font-sans font-semibold text-stone-800 hover:text-[#a85837]"
              >
                {l.label}
              </Link>
            ))}
            <span className="inline-flex items-center gap-1.5 px-3 min-h-[32px] rounded-full border border-[#ecd9c2] bg-[#f7efe6] text-[10px] font-sans font-bold uppercase tracking-wider text-stone-800">
              <ShieldCheck size={12} className="text-[#a85837]" aria-hidden />
              DSA &amp; AI Act Conforme
            </span>
          </nav>
        </div>
      </div>
      <ProtocolloContattoModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        oggettoPreset="Richiesta informazioni — 6amico.com"
      />
    </footer>
  );
}
